import React, { useState, useRef, useCallback } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';

interface SwipeableFeedbackCardProps {
  id: string;
  content: string;
  subContent?: string;
  type: 'blocker' | 'challenge' | 'goal' | 'value';
  onFeedback: (id: string, content: string, feedbackType: 'helpful' | 'not_helpful') => void;
  onDismiss?: (id: string) => void;
  className?: string;
  language?: string;
}

const FEEDBACK_LABELS = {
  Italian: { keep: 'Tieni', dismiss: 'Rimuovi', removed: 'Rimosso' },
  English: { keep: 'Keep', dismiss: 'Remove', removed: 'Removed' },
  Spanish: { keep: 'Mantener', dismiss: 'Eliminar', removed: 'Eliminado' },
  German: { keep: 'Behalten', dismiss: 'Entfernen', removed: 'Entfernt' },
  French: { keep: 'Garder', dismiss: 'Supprimer', removed: 'Supprimé' }
};

const SwipeableFeedbackCard: React.FC<SwipeableFeedbackCardProps> = ({
  id,
  content,
  subContent,
  type,
  onFeedback,
  onDismiss,
  className = '',
  language = 'English'
}) => {
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const startXRef = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);

  const labels = FEEDBACK_LABELS[language as keyof typeof FEEDBACK_LABELS] || FEEDBACK_LABELS.English;
  const SWIPE_THRESHOLD = 80;

  // Haptic feedback (if available)
  const triggerHaptic = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isRemoving) return;
    startXRef.current = e.touches[0].clientX;
    setIsDragging(true);
  }, [isRemoving]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || isRemoving) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - startXRef.current;

    // If confirm is shown, allow right swipe to close
    if (showConfirm) {
      if (diff > 0) {
        const newTranslate = -100 + diff;
        setTranslateX(Math.min(newTranslate, 0));
        if (diff >= SWIPE_THRESHOLD / 2) {
          triggerHaptic();
        }
      }
      return;
    }

    // Only allow left swipe (negative values)
    if (diff < 0) {
      // Add resistance when swiping too far
      const resistance = Math.abs(diff) > SWIPE_THRESHOLD ? 0.3 : 1;
      const newTranslate = diff * resistance;
      setTranslateX(Math.max(newTranslate, -120));

      // Haptic at threshold
      if (Math.abs(diff) >= SWIPE_THRESHOLD && Math.abs(translateX) < SWIPE_THRESHOLD) {
        triggerHaptic();
      }
    }
  }, [isDragging, isRemoving, translateX, showConfirm, triggerHaptic]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);

    // If confirm was shown and user swiped right to close
    if (showConfirm && translateX > -60) {
      setTranslateX(0);
      setShowConfirm(false);
      return;
    }

    if (Math.abs(translateX) >= SWIPE_THRESHOLD) {
      // Show confirm state
      setTranslateX(-100);
      setShowConfirm(true);
    } else {
      // Snap back
      setTranslateX(0);
    }
  }, [translateX, showConfirm]);

  const handleConfirmDismiss = useCallback(async () => {
    setIsRemoving(true);
    triggerHaptic();

    // Send feedback
    await onFeedback(id, content, 'not_helpful');

    // Animate out
    setTimeout(() => {
      if (onDismiss) {
        onDismiss(id);
      }
    }, 300);
  }, [id, content, onFeedback, onDismiss, triggerHaptic]);

  const handleCancel = useCallback(() => {
    setTranslateX(0);
    setShowConfirm(false);
  }, []);

  const handleKeep = useCallback(() => {
    onFeedback(id, content, 'helpful');
    triggerHaptic();
    // Visual feedback then reset
    setTranslateX(0);
    setShowConfirm(false);
  }, [id, content, onFeedback, triggerHaptic]);

  // Get type-specific colors
  const getColors = () => {
    switch (type) {
      case 'blocker':
      case 'challenge':
        return {
          bg: 'bg-amber-50',
          text: 'text-amber-900',
          subText: 'text-amber-700/70',
          icon: 'text-amber-500'
        };
      case 'goal':
        return {
          bg: 'bg-blue-50',
          text: 'text-blue-900',
          subText: 'text-blue-700/70',
          icon: 'text-blue-500'
        };
      case 'value':
        return {
          bg: 'bg-purple-50',
          text: 'text-purple-900',
          subText: 'text-purple-700/70',
          icon: 'text-purple-500'
        };
      default:
        return {
          bg: 'bg-gray-50',
          text: 'text-gray-900',
          subText: 'text-gray-700/70',
          icon: 'text-gray-500'
        };
    }
  };

  const colors = getColors();

  if (isRemoving) {
    return (
      <div
        className={`p-3 rounded-xl flex items-center justify-center transition-all duration-300 opacity-0 h-0 overflow-hidden ${colors.bg}`}
      >
        <span className="text-[12px] text-gray-400">{labels.removed}</span>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-xl ${className}`}>
      {/* Background action buttons (revealed on swipe) */}
      <div className="absolute inset-0 flex items-stretch justify-end">
        {/* Keep button (green) */}
        <button
          onClick={handleKeep}
          className="w-16 bg-emerald-500 flex items-center justify-center transition-opacity"
          style={{ opacity: showConfirm ? 1 : 0 }}
        >
          <Check size={20} className="text-white" />
        </button>

        {/* Dismiss button (red) */}
        <button
          onClick={handleConfirmDismiss}
          className="w-16 bg-red-500 flex items-center justify-center transition-opacity"
          style={{ opacity: Math.abs(translateX) > 20 ? 1 : 0 }}
        >
          <X size={20} className="text-white" />
        </button>
      </div>

      {/* Main card content */}
      <div
        ref={cardRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`p-3 ${colors.bg} relative z-10 transition-transform ${
          isDragging ? 'duration-0' : 'duration-200 ease-out'
        }`}
        style={{ transform: `translateX(${translateX}px)` }}
      >
        <div className="flex items-start gap-3">
          <AlertCircle size={14} className={`${colors.icon} mt-0.5 shrink-0`} />
          <div className="flex-1 min-w-0">
            <span className={`text-[13px] font-bold ${colors.text} block`}>
              {content}
            </span>
            {subContent && (
              <p className={`text-[12px] ${colors.subText} mt-0.5`}>
                {subContent}
              </p>
            )}
          </div>
        </div>

        {/* Swipe hint (only on mobile) */}
        {!showConfirm && translateX === 0 && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none lg:hidden">
            <div className="w-1 h-8 bg-black/20 rounded-full" />
          </div>
        )}
        {/* Close hint when confirm is shown on mobile */}
        {showConfirm && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-black/30 font-medium pointer-events-none lg:hidden animate-pulse">
            {language === 'Italian' ? '→ annulla' :
             language === 'Spanish' ? '→ cancelar' :
             language === 'German' ? '→ abbrechen' :
             language === 'French' ? '→ annuler' :
             '→ cancel'}
          </div>
        )}
      </div>

      {/* Confirmation overlay for desktop */}
      {showConfirm && (
        <div className="hidden lg:flex absolute inset-0 z-20 items-center justify-end gap-2 pr-3 bg-gradient-to-l from-white via-white/95 to-transparent">
          <button
            onClick={handleCancel}
            className="px-3 py-1.5 text-[12px] font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            Annulla
          </button>
          <button
            onClick={handleKeep}
            className="px-3 py-1.5 text-[12px] font-bold text-white bg-emerald-500 rounded-lg
                       hover:bg-emerald-600 transition-colors flex items-center gap-1"
          >
            <Check size={12} />
            {labels.keep}
          </button>
          <button
            onClick={handleConfirmDismiss}
            className="px-3 py-1.5 text-[12px] font-bold text-white bg-red-500 rounded-lg
                       hover:bg-red-600 transition-colors flex items-center gap-1"
          >
            <X size={12} />
            {labels.dismiss}
          </button>
        </div>
      )}
    </div>
  );
};

export default SwipeableFeedbackCard;
